/**
 * The recap's rows, in Jarea's form order (requirements §4.2). The recap page,
 * compare page, Excel and PDF exports all read this one list, so labels and
 * formats never drift between them.
 */
import type { RecapAssumptionsIn, RecapRow } from "../lib/eventReporting";
import { money } from "./format";

export type Fmt = "int" | "money" | "pct" | "hours";

export interface PerfRow {
  label: string;
  key: keyof RecapRow;
  fmt: Fmt;
  total?: boolean;
}

export const PERF_SECTIONS: { title: string; rows: PerfRow[] }[] = [
  {
    title: "Results",
    rows: [
      { label: "Leads", key: "leads", fmt: "int" },
      { label: "Appointments Set", key: "sets", fmt: "int" },
      { label: "Appointments Demoed", key: "demos", fmt: "int" },
      { label: "Sales #", key: "sales_count", fmt: "int" },
      { label: "Sales $", key: "sales_amount", fmt: "money" },
      { label: "Average Sale", key: "avg_sale", fmt: "money" },
      { label: "Closing %", key: "closing_pct", fmt: "pct" },
    ],
  },
  {
    title: "Booth costs",
    rows: [
      { label: "Space Rental", key: "cost_booth", fmt: "money" },
      { label: "Event Supplies", key: "cost_supplies", fmt: "money" },
      { label: "Misc. Venue", key: "cost_misc_venue", fmt: "money" },
      { label: "Total Booth Costs", key: "cost_booth_total", fmt: "money", total: true },
    ],
  },
  {
    title: "Employee costs",
    rows: [
      { label: "Employee Hours", key: "employee_hours", fmt: "hours" },
      { label: "Hourly Cost", key: "cost_hourly_pay", fmt: "money" },
      { label: "Team Commission", key: "commission", fmt: "money" },
      { label: "Manager Override (all sales)", key: "override", fmt: "money" },
      { label: "Manager Own Sales", key: "manager_own", fmt: "money" },
      { label: "Total Employee Costs", key: "employee_cost", fmt: "money", total: true },
    ],
  },
  {
    title: "Totals",
    rows: [
      { label: "Total Marketing Spend", key: "marketing_spend_total", fmt: "money", total: true },
      { label: "Cost of Marketing %", key: "cost_of_marketing_pct", fmt: "pct" },
      { label: "Demo Pay (not in spend)", key: "demo_pay", fmt: "money" },
    ],
  },
];

export const PERF_ROWS: PerfRow[] = PERF_SECTIONS.flatMap((s) => s.rows);

export interface AssumptionField {
  key: keyof RecapAssumptionsIn;
  label: string;
  kind: "text" | "int" | "money" | "long";
}

export const ASSUMPTION_FIELDS: AssumptionField[] = [
  { key: "venue", label: "Venue", kind: "text" },
  { key: "location", label: "Location", kind: "text" },
  { key: "show_attendance", label: "Show Attendance", kind: "int" },
  { key: "anticipated_net_revenue", label: "Anticipated Net Revenue", kind: "money" },
  { key: "total_vendors", label: "Total Vendors at Show", kind: "int" },
  { key: "weather", label: "Weather", kind: "text" },
  { key: "booth_location", label: "Booth Location", kind: "text" },
  { key: "booth_size", label: "Size of Booth", kind: "text" },
  { key: "notes", label: "Notes", kind: "long" },
];

/** Numeric value of a perf row, or null when there is no data (pending / unset rate). */
export function perfValue(row: RecapRow | null | undefined, key: keyof RecapRow): number | null {
  const v = row?.[key];
  return typeof v === "number" ? v : null;
}

export function fmtValue(v: number | null | undefined, fmt: Fmt): string {
  if (v === null || v === undefined) return "–";
  switch (fmt) {
    case "int": return Math.round(v).toLocaleString("en-US");
    case "money": return money(v); // whole dollars throughout the recap
    case "pct": return `${(v * 100).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
    case "hours": return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
}

/** Signed difference for the Δ column ("+3", "−$1,200", "+0.4 pts"). */
export function fmtDelta(live: number | null, saved: number | null, fmt: Fmt): string {
  if (live === null || saved === null) return "";
  const d = live - saved;
  if (Math.abs(d) < 1e-9) return "—";
  const sign = d > 0 ? "+" : "−";
  if (fmt === "pct") return `${sign}${Math.abs(d * 100).toLocaleString("en-US", { maximumFractionDigits: 1 })} pts`;
  return `${sign}${fmtValue(Math.abs(d), fmt)}`;
}

/** Strip a trailing year ("Woofstock 2025" → "woofstock") to suggest year-over-year pairs. */
export function eventFamily(subsource: string): string {
  return subsource.replace(/\s*\d{4}(\/\d{4})?\s*$/, "").trim().toLowerCase();
}
